import { defineStore } from "pinia";
import { computed, ref } from "vue";

import { getAdminAuth } from "@/shared/api/generated/admin/admin-auth/admin-auth";
import type { AdminAuthMe200, AdminAuthResource, Permission } from "@/shared/api/generated/admin/types";
import { toastSuccess } from "@/shared/utils/toast";

const api = getAdminAuth();

function adminFromMeResponse(res: AdminAuthMe200): AdminAuthResource {
  return res.admin;
}

export const useAuthStore = defineStore("auth", () => {
  const admin = ref<AdminAuthResource | null>(null);
  const isLoading = ref(false);

  const isAuthenticated = computed(() => admin.value !== null);
  const isSuperAdmin = computed(() => admin.value?.role === "super_admin");

  function can(permission: Permission): boolean {
    return admin.value?.permissions.includes(permission) ?? false;
  }

  async function initialize(): Promise<void> {
    const token = localStorage.getItem("admin_access_token");
    const refreshToken = localStorage.getItem("admin_refresh_token");

    if (!token && !refreshToken) {
      return;
    }

    try {
      isLoading.value = true;
      const res = await api.adminAuthMe();

      admin.value = adminFromMeResponse(res);
    } catch {
      localStorage.removeItem("admin_access_token");
      localStorage.removeItem("admin_refresh_token");
      admin.value = null;
    } finally {
      isLoading.value = false;
    }
  }

  async function login(email: string, password: string): Promise<void> {
    const res = await api.adminAuthLogin({ email, password });

    localStorage.setItem("admin_access_token", res.accessToken);
    localStorage.setItem("admin_refresh_token", res.refreshToken);

    admin.value = res.admin;
  }

  async function logout(): Promise<void> {
    try {
      await api.adminAuthLogout();
      toastSuccess("Successfully logged out.");
    } finally {
      localStorage.removeItem("admin_access_token");
      localStorage.removeItem("admin_refresh_token");
      admin.value = null;
      window.location.href = "/login";
    }
  }

  return { admin, isLoading, isAuthenticated, isSuperAdmin, can, initialize, login, logout };
});
